
import { useState, useEffect } from 'react';
import { Poll } from '@/types/poll';
import { useCategories } from '@/hooks/useCategories';

export const usePollDialog = (polls: Poll[]) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPoll, setCurrentPoll] = useState<Poll | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { categories } = useCategories(true);

  const handleOpenDialog = (poll?: Poll) => {
    console.log('Dialog opening with poll:', poll ? poll.id : 'new');
    
    if (poll) {
      // Create a deep copy of the poll to avoid reference issues
      const pollCopy = JSON.parse(JSON.stringify(poll));
      
      console.log('Poll before processing:', { 
        id: pollCopy.id,
        title: pollCopy.title,
        category: pollCopy.category,
        category_id: pollCopy.category_id,
        country_code: pollCopy.country_code,
        constituency_code: pollCopy.constituency_code
      });
      
      // Pre-sync category fields before opening dialog
      if (categories.length > 0) {
        // If we have category_id, make sure category name matches
        if (pollCopy.category_id) {
          const category = categories.find(c => c.id === pollCopy.category_id);
          if (category) {
            // If category name doesn't match or is missing, update it
            if (!pollCopy.category || pollCopy.category !== category.name) {
              console.log('Setting consistent category name:', category.name);
              pollCopy.category = category.name;
            }
          }
        } 
        // If we have category name but no ID, try to find ID
        else if (pollCopy.category) {
          const category = categories.find(c => c.name === pollCopy.category);
          if (category) {
            console.log('Setting category_id from name:', category.id);
            pollCopy.category_id = category.id;
          }
        }
      }
      
      console.log('Poll after processing:', { 
        id: pollCopy.id,
        title: pollCopy.title,
        category: pollCopy.category,
        category_id: pollCopy.category_id,
        country_code: pollCopy.country_code,
        constituency_code: pollCopy.constituency_code
      });
      
      setCurrentPoll(pollCopy);
      setIsEditing(true);
    } else {
      const now = new Date();
      // Set default close date to 7 days from now
      const closesAt = new Date();
      closesAt.setDate(now.getDate() + 7);
      
      // Get default category if available
      const defaultCategory = categories.length > 0 ? categories[0] : null;
      console.log('Default category for new poll:', defaultCategory);
      
      // Create a new poll with ALL required properties
      // Don't use a temporary ID for new polls as it causes issues with Supabase
      const newPoll: Poll = {
        id: '', // Set an empty string instead of a temporary ID
        title: '',
        description: '',
        options: [
          { id: 1, text: '', votes: 0 },
          { id: 2, text: '', votes: 0 }
        ],
        category: defaultCategory?.name || 'General',
        category_id: defaultCategory?.id || '',
        status: 'draft',
        created_at: now.toISOString(),
        closes_at: closesAt.toISOString(),
        country_code: null,
        constituency_code: null,
        total_votes: 0,
        linked_card_id: null,
        poll_number: null,
        anonymous_allowed: false,
        public_voting_allowed: false, // Default to false for new polls
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        endDate: closesAt.toISOString(),
        createdBy: null,
        questions: {
          options: [
            { id: 1, text: '', votes: 0 },
            { id: 2, text: '', votes: 0 }
          ],
          total_votes: 0
        },
        userHasVoted: false,
        countryCode: null,
        constituencyCode: null,
        categoryId: defaultCategory?.id || null
      };
      
      console.log('New poll created:', { 
        id: newPoll.id,
        category: newPoll.category,
        category_id: newPoll.category_id
      });
      
      setCurrentPoll(newPoll);
      setIsEditing(false);
    }
    setDialogOpen(true);
  };

  return {
    dialogOpen,
    setDialogOpen,
    currentPoll,
    setCurrentPoll,
    isEditing,
    handleOpenDialog
  };
};
